import {
  createContext,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { NonProfit, Tag } from "@ribondao/shared/types";
import { useNonProfitsContext } from "contexts/nonProfitsContext";

export interface ITagDonationContext {
  chosenTag: Tag | undefined;
  chosenTagIndex: number | undefined;
  setChosenTag: (tag: SetStateAction<Tag | undefined>) => void;
  setChosenTagIndex: (id: SetStateAction<number | undefined>) => void;
  nonProfitsTag: NonProfit[] | undefined;
}

export const TagDonationContext = createContext<ITagDonationContext>(
  {} as ITagDonationContext,
);
TagDonationContext.displayName = "TagDonationContext";

function TagDonationProvider({ children }: any) {
  const [chosenTag, setChosenTag] = useState<Tag | undefined>();

  const [chosenTagIndex, setChosenTagIndex] = useState<number | undefined>(0);
  const { shuffledNonProfits } = useNonProfitsContext();
  const [nonProfits, setNonProfits] = useState<NonProfit[] | undefined>();

  useEffect(() => {
    if (chosenTag) {
      const nonProfitsIds = shuffledNonProfits
        ?.filter((nonProfit) =>
          nonProfit.tags?.some((tag: any) => tag.id === chosenTag.id),
        )
        .map((nonProfit) => nonProfit.id);
      setNonProfits(
        shuffledNonProfits?.filter((nonProfit) =>
          nonProfitsIds?.includes(nonProfit.id),
        ),
      );
    } else if (shuffledNonProfits) setNonProfits(shuffledNonProfits);
  }, [chosenTag, shuffledNonProfits]);

  const TagDonationObject: ITagDonationContext = useMemo(
    () => ({
      chosenTag,
      setChosenTag,
      chosenTagIndex,
      setChosenTagIndex,
      nonProfitsTag: nonProfits,
    }),
    [
      chosenTag,
      setChosenTag,
      chosenTagIndex,
      setChosenTagIndex,
      nonProfits,
      shuffledNonProfits,
    ],
  );

  return (
    <TagDonationContext.Provider value={TagDonationObject}>
      {children}
    </TagDonationContext.Provider>
  );
}

export default TagDonationProvider;

export function useTagDonationContext() {
  const context = useContext(TagDonationContext);

  if (!context) {
    throw new Error(
      "useTagDonationContext must be used within a TagDonationProvider",
    );
  }

  return context;
}
